import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, Edit2, Save, X, Eye, EyeOff, Loader2, Info, ListChecks } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import { useAutoSave } from '../../hooks/useAutoSave';
import { getVariableHelpText } from '../../utils/templateVariables';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const ExpectationsEditor = () => {
  const [steps, setSteps] = useState([]);
  const [cta, setCta] = useState({
    button_text: '',
    section_title: '',
    section_subtitle: ''
  });
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [message, setMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStep, setNewStep] = useState({
    step: 1,
    title: '',
    description: '',
    visible: true
  });

  const ctaRef = useRef(cta);

  useEffect(() => {
    ctaRef.current = cta;
  }, [cta]);


  const performCtaSave = useCallback(async () => {
    const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/expectation-cta`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(ctaRef.current)
    });
    if (!response.ok) throw new Error('Failed to save CTA');
  }, []);

  const { isDirty, isSaving, scheduleAutosave, saveNow } = useAutoSave(performCtaSave, 5000);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [stepsRes, ctaRes] = await Promise.all([
        adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/expectation-steps`, {
          headers: getAuthHeaders()
        }),
        adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/expectation-cta`, {
          headers: getAuthHeaders()
        })
      ]);

      if (stepsRes.ok) {
        const stepsData = await stepsRes.json();
        setSteps(stepsData);
      }
      if (ctaRes.ok) {
        const ctaData = await ctaRes.json();
        setCta(ctaData);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCtaChange = (field, value) => {
    setCta(prev => ({ ...prev, [field]: value }));
    scheduleAutosave();
  };

  const handleCreateStep = async () => {
    if (!newStep.title.trim() || !newStep.description.trim()) {
      setMessage('Please fill in title and description');
      return;
    }

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/expectation-steps`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newStep,
          step: steps.length + 1
        })
      });

      if (response.ok) {
        setMessage('Step added!');
        setNewStep({ step: 1, title: '', description: '', visible: true });
        setShowAddForm(false);
        fetchData();
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to create');
      }
    } catch (error) {
      setMessage('Failed to create step');
    }
  };

  const handleUpdateStep = async (stepId) => {
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/expectation-steps/${stepId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        setMessage('Updated successfully!');
        setEditingId(null);
        setEditData({});
        fetchData();
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      setMessage('Failed to update step');
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Are you sure you want to delete this step?')) return;

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/expectation-steps/${stepId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage('Deleted successfully!');
        setSteps(steps.filter(s => s.id !== stepId));
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      setMessage('Failed to delete step');
    }
  };

  const toggleStepVisibility = async (step) => {
    const newVisibility = !step.visible;
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/expectation-steps/${step.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ visible: newVisibility })
      });

      if (response.ok) {
        setSteps(steps.map(s =>
          s.id === step.id ? { ...s, visible: newVisibility } : s
        ));
      }
    } catch (error) {
      console.error('Failed to toggle visibility');
    }
  };

  const startEditStep = (step) => {
    setEditingId(step.id);
    setEditData({
      step: step.step,
      title: step.title,
      description: step.description,
      visible: step.visible
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="What to Expect"
        description="Manage the expectation steps and CTA button"
        icon={<ListChecks className="h-6 w-6" />}
        actions={
          isSaving && (
            <span className="text-sm text-amber-700 flex items-center">
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              Saving...
            </span>
          )
        }
      />

      {/* Variable Help */}
      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">Dynamic Variables</p>
          <p className="text-sm text-blue-700 dark:text-blue-300">{getVariableHelpText()}</p>
        </div>
      </div>

      {/* CTA Settings */}
      <div className="admin-card admin-corner-accent border-2 border-stone-200 rounded-lg p-6 bg-gradient-to-r from-stone-50 to-amber-50/30">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <span className="text-amber-600">●</span>
              Section Settings & CTA Button
            </h3>
            <p className="text-sm text-stone-600 dark:text-stone-400">Configure the section header and call-to-action button</p>
          </div>
          {isDirty && !isSaving && (
            <Button onClick={saveNow} variant="adminGold" size="sm">
              <Save className="h-4 w-4 mr-2" />
              Save Now
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-800 dark:text-gray-200 font-medium">Section Title</Label>
            <Input
              value={cta.section_title}
              onChange={(e) => handleCtaChange('section_title', e.target.value)}
              placeholder="What to Expect"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
          </div>
          <div>
            <Label className="text-gray-800 dark:text-gray-200 font-medium">Section Subtitle</Label>
            <Input
              value={cta.section_subtitle}
              onChange={(e) => handleCtaChange('section_subtitle', e.target.value)}
              placeholder="Your journey to martial arts excellence starts here"
              className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
            />
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-gray-800 dark:text-gray-200 font-medium">CTA Button Text</Label>
          <Input
            value={cta.button_text}
            onChange={(e) => handleCtaChange('button_text', e.target.value)}
            placeholder="Get Started Today — QuickStart {{quickstart_fee}}"
            className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
          />
          <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">
            Use {"{{quickstart_fee}}"} to show the current QuickStart price
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.includes('!') && !message.includes('Failed')
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Steps Section */}
      <div className="admin-card border-2 border-stone-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <span className="text-amber-600">●</span>
              Expectation Steps
            </h3>
            <p className="text-sm text-stone-600 dark:text-stone-400">Manage the "What to Expect" steps</p>
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            variant="adminPrimary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Step
          </Button>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div className="mb-4 p-4 bg-background rounded-lg border border-stone-200 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-800 dark:text-gray-200 font-medium">Step #</Label>
                <Input
                  type="number"
                  value={newStep.step}
                  onChange={(e) => setNewStep({ ...newStep, step: parseInt(e.target.value) })}
                  className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-gray-800 dark:text-gray-200 font-medium">Title</Label>
                <Input
                  value={newStep.title}
                  onChange={(e) => setNewStep({ ...newStep, title: e.target.value })}
                  placeholder="e.g., Book Your Intro Appointment"
                  className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-800 dark:text-gray-200 font-medium">Description</Label>
              <Textarea
                value={newStep.description}
                onChange={(e) => setNewStep({ ...newStep, description: e.target.value })}
                placeholder="Describe this step... Use {{quickstart_fee}} for dynamic pricing"
                rows={2}
                className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateStep} variant="adminPrimary">
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button onClick={() => setShowAddForm(false)} variant="adminOutline">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Steps List */}
        <div className="space-y-3">
          {steps.length === 0 ? (
            <div className="text-center py-8 bg-stone-50 rounded-lg border border-stone-200">
              <p className="text-stone-600 dark:text-stone-400">No steps yet</p>
              <p className="text-sm text-stone-500 dark:text-stone-400">Click "Add Step" to create one</p>
            </div>
          ) : (
            steps.map((step) => (
              <Card key={step.id} className={`admin-card border-stone-200 p-4 ${!step.visible ? 'opacity-60' : ''}`}>
                {editingId === step.id ? (
                  // Edit Mode
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-gray-800 dark:text-gray-200 font-medium">Step #</Label>
                        <Input
                          type="number"
                          value={editData.step}
                          onChange={(e) => setEditData({ ...editData, step: parseInt(e.target.value) })}
                          className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <Label className="text-gray-800 dark:text-gray-200 font-medium">Title</Label>
                        <Input
                          value={editData.title}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                          className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-800 dark:text-gray-200 font-medium">Description</Label>
                      <Textarea
                        value={editData.description}
                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                        rows={2}
                        className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                      />
                    </div>
                    <div className="flex gap-2 pt-2 border-t border-stone-200">
                      <Button onClick={() => handleUpdateStep(step.id)} variant="adminPrimary" size="sm">
                        <Save className="h-3 w-3 mr-1" />
                        Save
                      </Button>
                      <Button onClick={() => { setEditingId(null); setEditData({}); }} variant="adminOutline" size="sm">
                        <X className="h-3 w-3 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                        {step.step}
                      </div>
                      <div>
                        <h4 className={`font-semibold ${step.visible ? 'text-gray-900 dark:text-gray-100' : 'text-stone-400'}`}>
                          {step.title}
                        </h4>
                        <p className={`text-sm mt-1 ${step.visible ? 'text-stone-600 dark:text-stone-400' : 'text-stone-400'}`}>
                          {step.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!step.visible && (
                        <span className="text-xs bg-stone-200 text-stone-600 dark:text-stone-400 px-2 py-0.5 rounded">Hidden</span>
                      )}
                      <Button
                        onClick={() => toggleStepVisibility(step)}
                        variant="ghost"
                        size="sm"
                        className="text-stone-600 dark:text-stone-400 hover:text-amber-600"
                      >
                        {step.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </Button>
                      <Button
                        onClick={() => startEditStep(step)}
                        variant="ghost"
                        size="sm"
                        className="text-stone-600 dark:text-stone-400 hover:text-amber-600"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleDeleteStep(step.id)}
                        variant="ghost"
                        size="sm"
                        className="text-stone-600 dark:text-stone-400 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpectationsEditor;
